/* ═══════════════════════════════════════════════════════════════
   ProjectService — Project CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Project } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const projectOpts: TableOpts = {
  jsonFields: ['masters', 'selections', 'formaTokens'],
};

export const ProjectService = {
  list: async (): Promise<Project[]> => cache.list<Project>('projects', projectOpts),

  save: async (project: Project): Promise<Project> => {
    const isNew = !project.id;
    const saved = await cache.save('projects', project, projectOpts);
    logActivity({
      action: isNew ? 'project.create' : 'project.update',
      entityType: 'project',
      entityId: saved.id,
      metadata: { name: saved.name, projectNumber: saved.projectNumber },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('projects', id);
    logActivity({ action: 'project.delete', entityType: 'project', entityId: id });
  },

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `projects.archive` (ec2-api/server.js). Mirrors
   *  ModuleService.setArchived exactly. */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'projects',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('projects');
    logActivity({
      action: archived ? 'project.archive' : 'project.restore',
      entityType: 'project',
      entityId: id,
    });
  },
};
